import clsx from "clsx";
import { useRef, useState } from "react";

import { ImageWithFallback } from "../ImageWithFallback";
import { LoadingSkeleton } from "../LoadingSkeleton";
import { type PrintArea } from "../../interfaces/blank";
import { LoadingSpinner } from "../LoadingSpinner";

export interface Props extends React.HTMLAttributes<HTMLDivElement> {
  imageSrc: string;
  imageSrc2?: string | null;
  preferLightweightImage?: boolean;
  customization?: {
    area?: PrintArea;
    imageSrc: string;
    printArea: PrintArea;
    productionPlacement?: { left: number; top: number; width: number; height: number };
  };
  imageAlt?: string;
  multipleImageSrc?: string[][];
  className?: string;
  mainImageWidth?: number;
  mainImageHeight?: number;
  customizationImageWidth?: number;
  customizationImageHeight?: number;
  loading?: boolean;
  loadingOverlay?: boolean;
  showSpinner?: boolean;
  onLoadImage?: (imageArea: { w: number; h: number }) => void;
  onImageError?: () => void;
  sizes?: string;
  priority?: boolean;
}

export const BlankImage = ({
  imageSrc,
  imageSrc2,
  preferLightweightImage = false,
  customization,
  imageAlt,
  multipleImageSrc,
  className,
  mainImageWidth = 200,
  mainImageHeight = 200,
  customizationImageWidth = 100,
  customizationImageHeight = 100,
  loading,
  loadingOverlay,
  showSpinner,
  onLoadImage,
  onImageError,
  sizes,
  priority,
  ...props
}: Props) => {
  const [failedLightweightImageSrc, setFailedLightweightImageSrc] = useState<string | null>(null);
  const shouldUseLightweightImage = preferLightweightImage && imageSrc2 && failedLightweightImageSrc !== imageSrc2;
  const baseImageSrc = shouldUseLightweightImage ? imageSrc2 : imageSrc;
  const [loadedBaseImageSrc, setLoadedBaseImageSrc] = useState<string | null>(null);
  const currentBaseImageSrc = useRef(baseImageSrc);

  currentBaseImageSrc.current = baseImageSrc;

  const handleBaseImageLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
    if (currentBaseImageSrc.current !== baseImageSrc) {
      return;
    }

    setLoadedBaseImageSrc(baseImageSrc);

    if (onLoadImage) {
      const rect = event.currentTarget.getBoundingClientRect();
      onLoadImage({ w: rect.width, h: rect.height });
    }
  };

  const handleBaseImageError = () => {
    if (currentBaseImageSrc.current !== baseImageSrc) {
      return;
    }

    setLoadedBaseImageSrc(null);

    if (shouldUseLightweightImage) {
      setFailedLightweightImageSrc(baseImageSrc);
      return;
    }

    onImageError?.();
  };

  if (loading) {
    return (
      <div className="relative h-full w-full">
        <LoadingSkeleton kind="image" className={className} />
        {showSpinner && <LoadingSpinner wrapperClassName="absolute top-1/2 left-1/2" />}
      </div>
    );
  }

  return (
    <div
      className={clsx(
        "relative flex h-[17rem] items-center justify-center",
        {
          "!w-full": multipleImageSrc,
          "w-auto": !customization,
          "w-fit": customization,
        },
        className,
      )}
      {...props}
    >
      {multipleImageSrc ? (
        multipleImageSrc.map((src, index) => {
          // cada imagem da pilha ocupa uma fração da largura total
          const perImgWidthCss = mainImageWidth / (multipleImageSrc.length + 1);
          const imgSizes = sizes || `${Math.round(perImgWidthCss)}px`;

          const [originalImageSrc, lightweightImageSrc] = src;
          const imageUrl = preferLightweightImage && lightweightImageSrc ? lightweightImageSrc : originalImageSrc;
          return (
            <ImageWithFallback
              key={index}
              src={imageUrl}
              sizes={imgSizes}
              alt={imageAlt || ""}
              width={mainImageWidth}
              height={mainImageHeight}
              className="absolute h-auto -translate-x-1/2 -translate-y-1/2"
              style={{
                width: `${200 / (multipleImageSrc.length + 1)}%`,
                left: `${((index + 1) * 100) / (1 + multipleImageSrc.length)}%`,
                top: `${((index + 1) * 100) / (1 + multipleImageSrc.length)}%`,
              }}
              loading="lazy"
            />
          );
        })
      ) : (
        <ImageWithFallback
          key={baseImageSrc}
          className="relative h-full w-auto object-cover z-10"
          src={baseImageSrc}
          sizes={sizes || `${Math.round(mainImageWidth)}px`}
          alt={imageAlt || ""}
          width={mainImageWidth}
          height={mainImageHeight}
          priority={priority}
          onLoad={handleBaseImageLoad}
          onError={handleBaseImageError}
        />
      )}

      {customization && loadedBaseImageSrc !== baseImageSrc && (
        <div
          aria-label="Carregando composição do produto"
          className="absolute inset-0 z-30"
          data-testid="blank-image-composition-loading"
          role="status"
        >
          <LoadingSkeleton kind="image" className="h-full w-full" />
        </div>
      )}

      {loadingOverlay && (
        <div aria-live="polite" className="absolute inset-0 z-30 flex items-center justify-center" role="status">
          <LoadingSkeleton kind="image" className="absolute inset-0 h-full w-full" />
          <LoadingSpinner size="3rem" className="relative text-brand-accent" />
          <span className="sr-only">Carregando imagem do produto</span>
        </div>
      )}

      {customization && loadedBaseImageSrc === baseImageSrc && (
        <div
          className="absolute aspect-square overflow-hidden z-20"
          style={{
            left: `${customization.printArea.x * 100}%`,
            top: `${customization.printArea.y * 100}%`,
            width: `${customization.printArea.w * 100}%`,
            height: `${customization.printArea.h * 100}%`,
          }}
        >
          <ImageWithFallback
            src={customization.imageSrc}
            sizes={`${Math.round(customizationImageWidth)}px`}
            width={customizationImageWidth}
            height={customizationImageHeight}
            alt={imageAlt || ""}
            className={customization.productionPlacement || customization.area ? "absolute" : "w-full h-full"}
            style={
              customization.productionPlacement
                ? {
                    left: `${customization.productionPlacement.left}%`,
                    top: `${customization.productionPlacement.top}%`,
                    width: `${customization.productionPlacement.width}%`,
                    height: `${customization.productionPlacement.height}%`,
                  }
                : customization.area
                  ? {
                      top: `${customization.area.y * 100 + 50}%`,
                      left: `${customization.area.x * 100 + 50}%`,
                      height: `${customization.area.h * 100}%`,
                      minHeight: `${customization.area.h * 100}%`,
                      width: `${customization.area.w * 100}%`,
                      minWidth: `${customization.area.w * 100}%`,
                      transform: "translate(-50%, -50%)",
                    }
                  : {}
            }
            loading="lazy"
          />
        </div>
      )}
    </div>
  );
};
